import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { SignedNumber } from '@dndbeyond/character-common-components/es';
import {
    AbilityIcon,
    Collapsible,
    DarkModeNegativeBonusNegativeSvg,
    DarkModePositiveBonusPositiveSvg,
    HtmlContent,
    NegativeBonusNegativeSvg,
    PositiveBonusPositiveSvg,
} from '@dndbeyond/character-components/es';
import {
    Ability,
    AbilityLookup,
    AbilityUtils,
    rulesEngineSelectors,
    characterActions,
    Constants,
    EntityValueLookup,
    FormatUtils,
    ModifierUtils,
    RuleData,
    RuleDataUtils,
    SituationalSavingThrowInfo,
    StatDataContract,
    ValueUtils,
    CharacterTheme,
} from '@dndbeyond/character-rules-engine/es';

import { sidebarActions } from '../../../actions';
import EditorBox from '../../../components/EditorBox';
import PaneInitFailureContent from '../../../components/Pane/PaneInitFailureContent';
import { SidebarHeader, SidebarHeaderPreview } from '../../../components/Sidebar';
import ValueEditor from '../../../components/ValueEditor';
import { appEnvSelectors } from '../../../selectors';
import { SharedAppState } from '../../../stores/typings';
import { PaneComponentEnum } from '../../../utils';
import { PaneIdentifiersAbilitySavingThrow } from '../../../utils/PaneIdentifier/typings';

interface Props extends DispatchProp {
    situationalBonusSavingThrowsLookup: Record<number, Array<SituationalSavingThrowInfo>>;
    abilityLookup: AbilityLookup;
    entityValueLookup: EntityValueLookup;
    ruleData: RuleData;
    identifiers: PaneIdentifiersAbilitySavingThrow | null;
    isReadonly: boolean;
    theme: CharacterTheme;
}
interface State {
    ability: Ability | null;
    abilityData: StatDataContract | null;
    situationalBonusSavingThrows: Array<SituationalSavingThrowInfo> | null;
}
class AbilitySavingThrowsPane extends React.PureComponent<Props, State> {
    constructor(props) {
        super(props);

        this.state = this.generateStateData(this.props);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        const { abilityLookup, situationalBonusSavingThrowsLookup, identifiers } = this.props;

        if (
            identifiers !== prevProps.identifiers ||
            abilityLookup !== prevProps.abilityLookup ||
            situationalBonusSavingThrowsLookup !== prevProps.situationalBonusSavingThrowsLookup
        ) {
            this.setState(this.generateStateData(this.props));
        }
    }

    generateStateData = (props: Props): State => {
        const { abilityLookup, situationalBonusSavingThrowsLookup, ruleData, identifiers } = props;

        const abilityDataLookup = RuleDataUtils.getStatsLookup(ruleData);
        let foundAbility: Ability | null | undefined = null;
        let foundAbilityData: StatDataContract | null | undefined = null;
        let foundSituationalBonusSavingThrows: Array<SituationalSavingThrowInfo> | null | undefined = null;

        if (identifiers) {
            foundAbility = abilityLookup[identifiers.id];
            foundAbilityData = abilityDataLookup[identifiers.id];
            foundSituationalBonusSavingThrows = situationalBonusSavingThrowsLookup[identifiers.id];
        }

        return {
            ability: foundAbility ? foundAbility : null,
            abilityData: foundAbilityData ? foundAbilityData : null,
            situationalBonusSavingThrows: foundSituationalBonusSavingThrows ? foundSituationalBonusSavingThrows : null,
        };
    };

    handleCustomDataUpdate = (key: number, value: any, source: string): void => {
        const { abilityData } = this.state;
        const { dispatch } = this.props;

        if (!abilityData) {
            return;
        }

        dispatch(
            characterActions.valueSet(
                key,
                value,
                source,
                ValueUtils.hack__toString(abilityData.id),
                ValueUtils.hack__toString(abilityData.entityTypeId),
            ),
        );
    };

    handleParentClick = (): void => {
        const { dispatch } = this.props;

        dispatch(sidebarActions.paneHistoryPush(PaneComponentEnum.SAVING_THROWS));
    };

    renderCustomize = (): React.ReactNode => {
        const { abilityData } = this.state;
        const { ruleData, entityValueLookup, isReadonly } = this.props;

        if (isReadonly || !abilityData) {
            return null;
        }

        let customizationValues: Array<Constants.AdjustmentTypeEnum> = [
            Constants.AdjustmentTypeEnum.SAVING_THROW_OVERRIDE,
            Constants.AdjustmentTypeEnum.SAVING_THROW_MAGIC_BONUS,
            Constants.AdjustmentTypeEnum.SAVING_THROW_MISC_BONUS,
            Constants.AdjustmentTypeEnum.SAVING_THROW_PROFICIENCY_LEVEL,
        ];

        return (
            <Collapsible layoutType={'minimal'} header="Customize" className="ct-ability-saving-throws-pane__customize">
                <EditorBox>
                    <ValueEditor
                        dataLookup={ValueUtils.getEntityData(
                            entityValueLookup,
                            ValueUtils.hack__toString(abilityData.id),
                            ValueUtils.hack__toString(abilityData.entityTypeId),
                        )}
                        onDataUpdate={this.handleCustomDataUpdate}
                        valueEditors={customizationValues}
                        ruleData={ruleData}
                    />
                </EditorBox>
            </Collapsible>
        );
    };

    renderSituationalBonusSummary = (situationalBonus: SituationalSavingThrowInfo): React.ReactNode => {
        const { theme } = this.props;
        let abilityNode: React.ReactNode;
        if (situationalBonus.type === Constants.SituationalBonusSavingThrowTypeEnum.MODIFIER) {
            let restriction = ModifierUtils.getRestriction(situationalBonus.extra);
            if (restriction) {
                restriction = restriction.trim();
                restriction = FormatUtils.lowerCaseLetters(restriction, 0);
                abilityNode = <React.Fragment>{restriction}</React.Fragment>;
            }
        }

        if (!abilityNode) {
            abilityNode = <React.Fragment>on saves</React.Fragment>;
        }

        const saveAmount = situationalBonus.value;
        let IconComponent: React.ComponentType<any>;
        if (situationalBonus.value >= 0) {
            IconComponent = theme?.isDarkMode ? DarkModePositiveBonusPositiveSvg : PositiveBonusPositiveSvg;
        } else {
            IconComponent = theme?.isDarkMode ? DarkModeNegativeBonusNegativeSvg : NegativeBonusNegativeSvg;
        }

        return (
            <div className="ct-ability-saving-throws-pane__situational-bonus" key={situationalBonus.key}>
                <IconComponent className="ct-ability-saving-throws-pane__situational-bonus-icon" />
                <span className="ct-ability-saving-throws-pane__situational-bonus-value">{saveAmount}</span>
                <span className="ct-ability-saving-throws-pane__situational-bonus-restriction">{abilityNode}</span>
            </div>
        );
    };

    renderSituationalBonuses = (): React.ReactNode => {
        const { situationalBonusSavingThrows } = this.state;

        if (!situationalBonusSavingThrows) {
            return null;
        }

        return (
            <div className="ct-ability-saving-throws-pane__situational">
                <div className="ct-ability-saving-throws-pane__situational-list">
                    {situationalBonusSavingThrows.map((situationalBonus) =>
                        this.renderSituationalBonusSummary(situationalBonus),
                    )}
                </div>
            </div>
        );
    };

    renderDescription = (): React.ReactNode => {
        const { ruleData } = this.props;

        let rulesText = RuleDataUtils.getRule(Constants.RuleKeyEnum.SAVING_THROWS, ruleData);

        if (!rulesText || !rulesText.description) {
            return null;
        }

        return <HtmlContent className="ct-ability-saving-throws-pane__description" html={rulesText.description} />;
    };

    render() {
        const { ability, abilityData } = this.state;
        const { theme } = this.props;

        if (!ability || !abilityData) {
            return <PaneInitFailureContent />;
        }
        const statId = AbilityUtils.getId(ability);
        const statKey = AbilityUtils.getStatKey(ability);

        let sidebarPreviewClassNames: Array<string> = ['ct-ability-saving-throws-pane__icon'];
        if (abilityData.name) {
            sidebarPreviewClassNames.push(`ct-ability-saving-throws-pane__icon--${statKey}`);
        }

        let sidebarPreviewNode: React.ReactNode = (
            <SidebarHeaderPreview>
                <AbilityIcon
                    className={sidebarPreviewClassNames.join(' ')}
                    statId={statId}
                    themeMode={theme.isDarkMode ? 'light' : 'dark'}
                />
            </SidebarHeaderPreview>
        );

        return (
            <div className="ct-ability-saving-throws-pane" key={statId}>
                <SidebarHeader
                    preview={sidebarPreviewNode}
                    parent="Saving Throws"
                    onParentClick={this.handleParentClick}
                >
                    {abilityData.name} Saving Throw
                    <span className="ct-ability-saving-throws-pane__modifier">
                        <SignedNumber number={AbilityUtils.getSave(ability)} />
                    </span>
                </SidebarHeader>
                {this.renderCustomize()}
                {this.renderSituationalBonuses()}
                {this.renderDescription()}
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        ruleData: rulesEngineSelectors.getRuleData(state),
        abilityLookup: rulesEngineSelectors.getAbilityLookup(state),
        entityValueLookup: rulesEngineSelectors.getCharacterValueLookupByEntity(state),
        situationalBonusSavingThrowsLookup: rulesEngineSelectors.getSituationalBonusSavingThrowsLookup(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
    };
}

export default connect(mapStateToProps)(AbilitySavingThrowsPane);
